import React from 'react';
import { 
  Box, 
  Button, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia,
  Paper,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  AccountBalanceOutlined as AccountBalanceIcon,
  TrendingUpOutlined as TrendingUpIcon,
  SecurityOutlined as SecurityIcon,
  SpeedOutlined as SpeedIcon,
  AccessTimeOutlined as AccessTimeIcon,
  CreditCardOffOutlined as CreditCardOffIcon,
  SupportAgentOutlined as SupportAgentIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  WhatsApp as WhatsAppIcon,
  FormatQuoteOutlined as FormatQuoteIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const HomePage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isAuthenticated } = useAuth();

  const testimonials = [
    {
      quote: "Facto nos ayudó a mantener el flujo de caja cuando más lo necesitábamos. En menos de 48 horas teníamos el dinero en nuestra cuenta.",
      name: "Jorge González",
      role: "Gerente Financiero",
      company: "Blue Trade",
      image: "/assets/cliente_feliz_01.png"
    },
    {
      quote: "Como inversionista, encontré en Facto una forma segura y rentable de diversificar. Las facturas verificadas me dan tranquilidad.",
      name: "Carlos Benítez",
      role: "Inversionista",
      company: "",
      image: "/assets/cliente_feliz_02.png"
    }
  ];

  const benefits = [
    {
      title: 'Flujo de Efectivo Inmediato',
      description: 'Convierte tus facturas en efectivo inmediato sin esperar los términos de pago.',
      icon: <SpeedIcon fontSize="large" color="primary" />,
      forSellers: true
    },
    {
      title: 'Tarifas Competitivas',
      description: 'Obtén mejores tarifas a través de nuestro mercado competitivo de compradores.',
      icon: <TrendingUpIcon fontSize="large" color="primary" />,
      forSellers: true
    },
    {
      title: 'Retornos Atractivos',
      description: 'Obtén retornos atractivos invirtiendo en facturas verificadas de empresas confiables.',
      icon: <AccountBalanceIcon fontSize="large" color="primary" />,
      forBuyers: true
    },
    {
      title: 'Inversiones Seguras',
      description: 'Todas las facturas son verificadas y aseguradas a través de nuestro riguroso proceso de verificación.',
      icon: <SecurityIcon fontSize="large" color="primary" />,
      forBuyers: true
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Paper 
        component="section"
        aria-label="Hero section"
        sx={{ 
          position: 'relative',
          background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
          color: 'white',
          mb: 0,
          py: { xs: 6, md: 10 },
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            background: 'radial-gradient(circle at top right, rgba(255, 255, 255, 0.1) 0%, transparent 60%)',
            pointerEvents: 'none',
          }
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              {/* Brand name - smaller, secondary */}
              <Typography 
                variant="overline" 
                color="inherit" 
                sx={{ 
                  opacity: 0.9,
                  letterSpacing: 2,
                  fontSize: '1rem',
                  mb: 1,
                  display: 'block'
                }}
              >
                Facto
              </Typography>
              
              {/* Main headline - benefit-driven */}
              <Typography 
                component="h1" 
                variant={isMobile ? 'h4' : 'h3'}
                color="inherit" 
                sx={{ 
                  fontWeight: 'bold',
                  lineHeight: 1.2,
                  mb: 2
                }}
              >
                Convertí tus facturas en capital en 24-72 horas
              </Typography>
              
              {/* Subcopy with key differentiators */}
              <Typography 
                variant="h6" 
                color="inherit" 
                sx={{ 
                  opacity: 0.95,
                  mb: 3,
                  fontWeight: 400
                }}
              >
                Sin endeudarte. Sin trámites complicados. Con soporte personalizado.
              </Typography>
              
              {/* Value props - quick scan */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleOutlineIcon sx={{ mr: 1, fontSize: 20, opacity: 0.9 }} />
                  <Typography variant="body1" sx={{ opacity: 0.95 }}>
                    Facturas verificadas por expertos
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleOutlineIcon sx={{ mr: 1, fontSize: 20, opacity: 0.9 }} />
                  <Typography variant="body1" sx={{ opacity: 0.95 }}>
                    Múltiples ofertas competitivas
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleOutlineIcon sx={{ mr: 1, fontSize: 20, opacity: 0.9 }} />
                  <Typography variant="body1" sx={{ opacity: 0.95 }}>
                    Plataforma 100% digital
                  </Typography>
                </Box>
              </Box>
              
              {/* CTAs with clear hierarchy - REQ-CTA-01, REQ-CTA-02, REQ-CTA-03 */}
              {!isAuthenticated && (
                <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
                  <Button 
                    component="a"
                    href="https://wa.me/595985298994?text=Hola%2C%20me%20interesa%20saber%20m%C3%A1s%20sobre%20Facto"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained" 
                    size="large"
                    startIcon={<WhatsAppIcon />}
                    aria-label="Contactar con asesor por WhatsApp"
                    sx={{ 
                      backgroundColor: 'white',
                      color: 'primary.main',
                      fontWeight: 600,
                      boxShadow: 3,
                      '&:hover': {
                        backgroundColor: 'grey.100',
                      }
                    }} 
                    data-analytics="cta_hero_whatsapp"
                  >
                    Hablar con un asesor
                  </Button>
                  <Button 
                    component={RouterLink} 
                    to="/register" 
                    variant="outlined" 
                    size="large"
                    aria-label="Crear cuenta gratuita"
                    sx={{ 
                      borderColor: 'rgba(255,255,255,0.7)',
                      color: 'white',
                      borderWidth: 2,
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        borderWidth: 2,
                      }
                    }}                    
                    data-analytics="cta_hero_register"
                  >
                    Crear cuenta gratis
                  </Button>
                </Box>
              )}
              {isAuthenticated && (
                <Box>
                  <Button 
                    component={RouterLink} 
                    to="/dashboard" 
                    variant="contained" 
                    size="large"
                    aria-label="Ir al panel de control"
                    sx={{ 
                      backgroundColor: 'white',
                      color: 'primary.main',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: 'grey.100',
                      }
                    }}
                  >
                    Ir al panel
                  </Button>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Box 
                component="img"
                src="/assets/jovenes_01.png"
                alt="Convierte facturas en capital"
                sx={{ 
                  width: '100%',
                  height: 'auto',
                  maxHeight: '400px',
                  objectFit: 'contain'
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Paper>

      {/* Trust indicators - quick stats */}
      <Container 
        component="section" 
        maxWidth="lg" 
        aria-label="Estadísticas clave"
        sx={{ py: 8, backgroundColor: 'background.accent' }}
      >
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <AccessTimeIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h5" fontWeight="bold" color="primary">
                24-72h
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tiempo de pago
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CreditCardOffIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h5" fontWeight="bold" color="primary">
                0% deuda
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No es un préstamo
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <SecurityIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h5" fontWeight="bold" color="primary">
                100%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Facturas verificadas
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <SupportAgentIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h5" fontWeight="bold" color="primary">
                1:1
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Soporte personalizado
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* What is Factoring Section */}
      <Container 
        component="section" 
        maxWidth="lg" 
        aria-labelledby="factoring-heading"
        sx={{ py: 8, backgroundColor: 'background.paper' }}
      >
        <Typography 
          id="factoring-heading"
          component="h2" 
          variant="h3" 
          align="center" 
          color="textPrimary" 
          gutterBottom
          sx={{ mb: 2 }}
        >
          ¿Qué es el Factoraje?
        </Typography>
        <Typography variant="body1" align="center" color="textSecondary" paragraph sx={{ maxWidth: '800px', mx: 'auto', mb: 6 }}>
          El factoraje es una transacción financiera donde una empresa vende sus cuentas por cobrar (facturas) a un tercero con descuento para satisfacer sus necesidades inmediatas de efectivo.
        </Typography>
        <Grid container spacing={4} sx={{ mt: 4 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom color="primary" sx={{ fontWeight: 600 }}>
              Cómo Funciona
            </Typography>
            <Typography paragraph>
              1. <strong>Subís tu factura</strong> a nuestra plataforma segura
            </Typography>
            <Typography paragraph>
              2. <strong>Verificamos</strong> con nuestro equipo de expertos en 24h
            </Typography>
            <Typography paragraph>
              3. <strong>Recibís ofertas</strong> de múltiples inversionistas
            </Typography>
            <Typography paragraph>
              4. <strong>Aceptás la mejor oferta</strong> y cobras de inmediato
            </Typography>            
          </Grid>
          <Grid item xs={12} md={6}>
            <Card elevation={3}>
              <CardMedia
                component="img"
                height="240"
                image="/assets/process_01.png"
                alt="How factoring works"
              />
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Testimonials Section */}
      <Box 
        component="section" 
        aria-labelledby="testimonials-heading"
        sx={{ bgcolor: 'background.subtle', py: 8 }}
      >
        <Container maxWidth="lg">
          <Typography 
            id="testimonials-heading"
            component="h2" 
            variant="h3" 
            align="center" 
            color="textPrimary" 
            gutterBottom
            sx={{ mb: 2 }}
          >
            Lo que dicen nuestros clientes
          </Typography>
          <Typography variant="body1" align="center" color="textSecondary" sx={{ mb: 6 }}>
            Empresas e inversionistas que ya confían en Facto
          </Typography>
          
          <Grid container spacing={4} justifyContent="center">
            {testimonials.map((testimonial, index) => (
              <Grid item key={index} xs={12} md={6}>
                <Card 
                  component="article"
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    pt: 2
                  }}
                  elevation={2}
                >
                  <FormatQuoteIcon 
                    sx={{ 
                      position: 'absolute',
                      top: 16,
                      left: 16,
                      fontSize: 40,
                      color: 'primary.light',
                      opacity: 0.3
                    }} 
                  />
                  <CardContent sx={{ flexGrow: 1, pt: 4 }}>
                    <Typography 
                      variant="body1" 
                      color="text.secondary"
                      sx={{ 
                        fontStyle: 'italic',
                        mb: 3,
                        lineHeight: 1.7
                      }}
                    >
                      "{testimonial.quote}"
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
                      <Box
                        component="img"
                        src={testimonial.image}
                        alt={testimonial.name}
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: '50%',
                          objectFit: 'cover',
                          mr: 2
                        }}
                      />
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {testimonial.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {testimonial.role}{testimonial.company && `, ${testimonial.company}`}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box 
        component="section" 
        aria-labelledby="benefits-heading"
        sx={{ bgcolor: 'background.paper', py: 8 }}
      >
        <Container maxWidth="lg">
          <Typography 
            id="benefits-heading"
            component="h2" 
            variant="h3" 
            align="center" 
            color="textPrimary" 
            gutterBottom
            sx={{ mb: 6 }}
          >
            Beneficios
          </Typography>
          
          {/* Benefits for Sellers */}
          <Typography 
            component="h3" 
            variant="h4" 
            color="primary" 
            align="center" 
            sx={{ mt: 6, mb: 4, fontWeight: 600 }}
          >
            Para Vendedores
          </Typography>
          <Grid container spacing={4}>
            {benefits.filter(b => b.forSellers).map((benefit, index) => (
              <Grid item key={index} xs={12} sm={6}>
                <Card component="article" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      {benefit.icon}
                    </Box>
                    <Typography gutterBottom variant="h5" component="h3" align="center" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {benefit.title}
                    </Typography>
                    <Typography align="center" color="text.secondary">
                      {benefit.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          {/* Benefits for Buyers */}
          <Typography 
            component="h3" 
            variant="h4" 
            color="primary" 
            align="center" 
            sx={{ mt: 6, mb: 4, fontWeight: 600 }}
          >
            Para Compradores
          </Typography>
          <Grid container spacing={4}>
            {benefits.filter(b => b.forBuyers).map((benefit, index) => (
              <Grid item key={index} xs={12} sm={6}>
                <Card component="article" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      {benefit.icon}
                    </Box>
                    <Typography gutterBottom variant="h5" component="h3" align="center" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {benefit.title}
                    </Typography>
                    <Typography align="center" color="text.secondary">
                      {benefit.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Call to Action */}
      <Container 
        component="section" 
        maxWidth="md" 
        aria-labelledby="cta-heading"
        sx={{ 
          py: 8, 
          textAlign: 'center',
          background: 'linear-gradient(180deg, rgba(255, 107, 53, 0.03) 0%, rgba(255, 107, 53, 0.08) 100%)',
          borderRadius: 3,
          my: 8
        }}
      >
        <Typography id="cta-heading" component="h2" variant="h4" gutterBottom sx={{ mb: 2 }}>
          ¿Listo para comenzar?
        </Typography>
        <Typography variant="body1" color="textSecondary" paragraph sx={{ mb: 4 }}>
          Únete a nuestra creciente comunidad de empresas e inversionistas hoy.
        </Typography>
        {!isAuthenticated && (
          <Box sx={{ mt: 4, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, justifyContent: 'center' }}>
            <Button 
              component={RouterLink} 
              to="/register" 
              variant="contained" 
              color="primary" 
              size="large"
              aria-label="Registrarse en Facto"
            >
              Registrarse ahora
            </Button>
            <Button 
              component={RouterLink} 
              to="/login" 
              variant="outlined" 
              color="primary" 
              size="large"
              aria-label="Iniciar sesión en Facto"
            >
              Iniciar sesión
            </Button>
          </Box>
        )}
        {isAuthenticated && (
          <Box sx={{ mt: 4 }}>
            <Button 
              component={RouterLink} 
              to="/dashboard" 
              variant="contained" 
              color="primary" 
              size="large"
              aria-label="Ir al panel de control"
            >
              Ir al panel
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default HomePage;
